import toGeoJSON from './togeojson';

const kmlToLongdoMap = function(map, kml, options = {}) {
  if (!options.hasOwnProperty('markerOptions')) {
    options.markerOptions = {};
  }

  if (!options.hasOwnProperty('geometryOptions')) {
    options.geometryOptions = {};
  }

  const resultOverlays = {
    Marker: [],
    Polygon: [],
    Polyline: []
  };

  if (!map || typeof map !== 'object') {
    throw new Error('Invalid Longdo Map object');
  }

  if (!kml || typeof kml !== 'string') {
    throw new Error('Unable to read KML.');
  }

  if (typeof toGeoJSON === 'undefined' || typeof toGeoJSON.kml !== 'function') {
    throw new Error('togeojson.js is not found.');
  }

  kml = getKmlData(kml);
  const geoJson = toGeoJSON.kml(kml);
  const { features } = geoJson;
  const allLon = [];
  const allLat = [];

  for (const feature of features) {
    addOverlayByType(feature);
  }

  if (allLon.length > 0 && allLat.length > 0) {
    const minMaxLon = minMaxArr(allLon);
    const minMaxLat = minMaxArr(allLat);
    map.bound({
      minLon: minMaxLon.min,
      minLat: minMaxLat.min,
      maxLon: minMaxLon.max,
      maxLat: minMaxLat.max
    });
  }

  function minMaxArr(arr) {
    let max = arr[0];
    let min = arr[0];
    for (let i = 0; i < arr.length; i++) {
      if (max < arr[i]) {
        max = arr[i];
      }
      if (min > arr[i]) {
        min = arr[i];
      }
    }
    return { max, min };
  }

  function setDonutGeomData(data) {
    const secArr = [];
    let passSec = false;
    let replaceIndex = 0;
    for (const point of data) {
      if (passSec) {
        secArr.push(point);
      } else {
        passSec = point === null;
        replaceIndex++;
      }
    }
    if (isClockwise(secArr)) {
      secArr.reverse();
    }
    data.splice(replaceIndex);
    data = data.concat(secArr);
    return data;
  }

  function isClockwise(arr = []) {
    let sum = 0;
    const pop = null;
    let lastIndex = arr.length - 1;
    if (arr.length > 0) {
      if (arr[0].lat === arr[arr.length - 1].lat && arr[0].lon === arr[arr.length - 1].lon) {
        lastIndex--;
      }
      for (let i = 0; i < lastIndex + 1; i++) {
        if (i === lastIndex) {
          sum += (arr[0].lat - arr[i].lat) * (arr[0].lon + arr[i].lon);
        } else {
          sum += (arr[i + 1].lat - arr[i].lat) * (arr[i + 1].lon + arr[i].lon);
        }
      }
    }
    return sum >= 0;
  }

  function addOverlayByType(feature) {
    const { type } = feature.geometry;
    switch (type) {
      case 'Point':
        addMarker(feature);
        break;

      case 'Polygon':
        addPolygon(feature);
        break;

      case 'LineString':
        addPolyline(feature);
        break;

      case 'GeometryCollection':
        addGeometryCollection(feature);
        break;

      default:
        console.warn(`${type} is not supported.`);
        break;
    }
  }

  function getKmlData(kml) {
    if (typeof kml === 'string') {
      kml = new DOMParser().parseFromString(kml, 'text/xml');
      return kml;
    }
  }

  function addGeometryCollection(feature) {
    const features = feature.geometry.geometries;
    for (const item of features) {
      const tempObj = {
        geometry: item,
        properties: feature.properties
      };
      addOverlayByType(tempObj);
    }
  }

  function addMarker(feature) {
    const pos = feature.geometry.coordinates;
    const props = feature.properties;
    const lon = pos[0];
    const lat = pos[1];
    const marker = new longdo.Marker(
      { lon, lat },
      {
        title: props.name,
        detail: props.description,
        ...options.markerOptions
      }
    );
    map.Overlays.add(marker);
    resultOverlays.Marker.push(marker);
    allLon.push(pos[0]);
    allLat.push(pos[1]);
  }

  function addPolygon(feature) {
    const pos = feature.geometry.coordinates;
    const props = feature.properties;
    const lineColor = hexToRgb(props.stroke);
    const fillColor = hexToRgb(props.fill);
    const info = {
      title: props.name,
      detail: props.description,
      lineWidth: 1,
      ...options.geometryOptions
    };
    if (setRgbaColor(lineColor)) {
      info.lineColor = setRgbaColor(lineColor);
    }
    if (setRgbaColor(fillColor, 0.4)) {
      info.fillColor = setRgbaColor(fillColor, 0.4);
    }
    const isDonut = pos.length === 2;
    let locationList = [];
    let count = 0;
    for (const item of pos) {
      if (!isDonut) {
        locationList = [];
      } else if (isDonut && locationList.length > 0) {
        locationList.push(null);
      }
      for (const row of item) {
        const lon = row[0];
        const lat = row[1];
        locationList.push({ lon, lat });
        allLon.push(lon);
        allLat.push(lat);
      }
      if (!isDonut) {
        const geom = new longdo.Polygon(locationList, info);
        map.Overlays.add(geom);
        resultOverlays.Polygon.push(geom);
      } else if (isDonut && count === 1) {
        locationList = setDonutGeomData(locationList);
        const geom = new longdo.Polygon(locationList, info);
        map.Overlays.add(geom);
        resultOverlays.Polygon.push(geom);
      }
      count++;
    }
  }

  function addPolyline(feature) {
    const pos = feature.geometry.coordinates;
    const props = feature.properties;
    const lineColor = hexToRgb(props.stroke);
    const info = {
      title: props.name,
      detail: props.description,
      lineWidth: 1,
      ...options.geometryOptions
    };
    if (setRgbaColor(lineColor)) {
      info.lineColor = setRgbaColor(lineColor);
    }
    const locationList = [];
    for (const item of pos) {
      const lon = item[0];
      const lat = item[1];
      locationList.push({ lon, lat });
      allLon.push(lon);
      allLat.push(lat);
    }
    const geom = new longdo.Polyline(locationList, info);
    map.Overlays.add(geom);
    resultOverlays.Polyline.push(geom);
  }

  function setRgbaColor(colorObj, a = 1) {
    let rgbStr = null;
    if (colorObj !== null) {
      rgbStr = `rgba(${colorObj.r}, ${colorObj.g}, ${colorObj.b}, ${a})`;
    }
    return rgbStr;
  }

  function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null;
  }

  return resultOverlays;
};

export default kmlToLongdoMap;
